import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import {
  ShoppingBag,
  Search,
  Filter,
  Grid,
  List,
  ArrowLeft,
  ArrowRight,
  Star,
  ShoppingCart,
  X,
  Store
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_bizcommerce-19/artifacts/7hv4d1j9_LOGO%20PNG.png";

const StorefrontPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchMerchants();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/public/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/public/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await fetch(`${API}/public/merchants`);
      if (response.ok) {
        const data = await response.json();
        setMerchants(data);
      }
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || product.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img src={COMPANY_LOGO} alt="V" className="h-10 w-auto" />
              <div className="h-8 w-px bg-slate-300" />
              <span className="font-brand text-xl font-bold text-slate-900">VEYRONIX</span>
            </Link>

            {/* Search Bar */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 bg-slate-100 border-slate-200 focus:bg-white"
                  data-testid="search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {user ? (
                <Button
                  onClick={() => navigate(user.role === "merchant" ? "/merchant" : "/dashboard")}
                  className="bg-indigo-600 hover:bg-indigo-700 rounded-full"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-600" data-testid="login-btn">Sign In</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full" data-testid="register-btn">
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48" data-testid="category-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <span className="text-slate-500 text-sm">
                {filteredProducts.length} products found
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-lg transition-colors ${viewMode === "grid" ? "bg-indigo-100 text-indigo-600" : "text-slate-400 hover:bg-slate-100"}`}
                data-testid="grid-view-btn"
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-lg transition-colors ${viewMode === "list" ? "bg-indigo-100 text-indigo-600" : "text-slate-400 hover:bg-slate-100"}`}
                data-testid="list-view-btn"
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Featured Stores Section */}
        {!searchTerm && selectedCategory === "all" && merchants.length > 0 && (
          <section className="mb-12" data-testid="featured-stores-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Featured Stores</h2>
              <span className="text-sm text-slate-500">{merchants.length} stores</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {merchants.slice(0, 4).map((merchant, index) => {
                const merchantName = merchant.business_name || merchant.name || `${merchant.first_name || ""} ${merchant.last_name || ""}`.trim() || "Store";
                const merchantLogo = merchant.logo_url ? 
                  (merchant.logo_url.startsWith('data:') ? merchant.logo_url :
                   merchant.logo_url.startsWith('http') ? merchant.logo_url : 
                   `${API}${merchant.logo_url}`) 
                  : merchant.profile_image;
                return (
                  <Link
                    key={merchant.user_id}
                    to={`/store/${merchant.user_id}`}
                    className="group bg-white rounded-xl border border-slate-200 p-5 hover:border-indigo-300 hover:shadow-md transition-all"
                    data-testid={`store-card-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      {merchantLogo ? (
                        <img 
                          src={merchantLogo} 
                          alt={merchantName}
                          className="w-14 h-14 rounded-xl object-cover bg-slate-50 border border-slate-100"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center">
                          <Store className="h-7 w-7 text-indigo-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                          {merchantName}
                        </h3>
                        <p className="text-sm text-slate-500 truncate">
                          {merchant.tagline || merchant.description || "View store"}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </Link>
                );
              })}
            </div>
            {merchants.length > 4 && (
              <div className="text-center mt-4">
                <Button variant="outline" className="rounded-full" onClick={() => navigate("/stores")}>
                  View All {merchants.length} Stores
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Products Section Title */}
        {!searchTerm && selectedCategory === "all" && (
          <h2 className="text-2xl font-bold text-slate-900 mb-6">All Products</h2>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">No products found</h3>
            <p className="text-slate-500 mb-6">Try adjusting your search or filter criteria.</p>
            <Button
              onClick={() => { setSearchTerm(""); setSelectedCategory("all"); }}
              variant="outline"
              className="rounded-full"
            >
              Clear Filters
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            {filteredProducts.map((product, index) => (
              <div
                key={product.product_id}
                onClick={() => handleProductClick(product.product_id)}
                className="group bg-white rounded-2xl border border-slate-200 overflow-hidden cursor-pointer card-hover"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`product-card-${index}`}
              >
                <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                  {(product.image_url || product.images?.[0]) ? (
                    <img
                      src={product.image_url || product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="h-12 w-12 text-slate-300" />
                    </div>
                  )}
                  {product.is_subscription && (
                    <span className="absolute top-3 left-3 px-2 py-1 bg-indigo-600 text-white text-xs font-medium rounded-full">
                      Subscription
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-slate-900 mb-1 line-clamp-1">
                    {product.name}
                  </h3>
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-bold text-lg text-indigo-600">
                      £{product.price.toFixed(2)}
                      {product.is_subscription && (
                        <span className="text-sm font-normal text-slate-500">
                          /{product.subscription_interval}
                        </span>
                      )}
                    </span>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 rounded-full h-9 px-4"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(product.product_id);
                      }}
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product, index) => (
              <div
                key={product.product_id}
                onClick={() => handleProductClick(product.product_id)}
                className="bg-white rounded-xl border border-slate-200 p-4 flex gap-4 cursor-pointer hover:border-indigo-300 transition-colors"
                data-testid={`product-list-${index}`}
              >
                <div className="w-24 h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                  {(product.image_url || product.images?.[0]) ? (
                    <img
                      src={product.image_url || product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="h-8 w-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 mb-1">
                        {product.name}
                        {product.is_subscription && (
                          <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-600 text-xs font-medium rounded-full">
                            Subscription
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-500 line-clamp-2">
                        {product.description}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="font-heading font-bold text-lg text-indigo-600">
                        £{product.price.toFixed(2)}
                      </span>
                      {product.is_subscription && (
                        <span className="block text-sm text-slate-500">
                          per {product.subscription_interval}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default StorefrontPage;
